import { supertypeClass, property, remote, amorphicStatic } from '@haventech/amorphic';
import { Everything } from '@hit/amorphic-types';

import _ from 'lodash';
import { Utils } from '../Utils';
import { Insurers } from '../static/Insurers';
import { PremiumService, PremiumServiceResponse } from '../services/PremiumService';
import { ProductApiTypes } from '@hit/policy-admin-types';
import { RateClasses } from '../../../common/js/static/RateClasses';
import { QuoteEngine, QuoteEngineResponse } from '../QuoteEngine';
import { PremiumResponse, ProductServiceClient, PremiumRequest } from '../productService/ProductServiceClient';
import { Product } from '../static/Product';

declare var window;

@supertypeClass({ toClient: true, toServer: true })
export class Quotes extends Everything {
  @property({ isLocal: true, persist: false })
  quote;

  @property({ isLocal: true })
  quotes = {};

  @property({ isLocal: true })
  previousQuote;

  @property({ isLocal: true })
  currentQuoteKey: string;

  @property({ isLocal: true })
  filteredQuotes = {};

  @property({ toClient: false, toServer: false })
  hlQuotesJson;

  @property()
  quoteCache = {};

  @property()
  quoted;

  constructor() {
    super();
  }

  static new() {
    return new Quotes().init();
  }

  init() {
    return this;
  }

  compute() {
    this.quoted = false;
  }

  structureQuotes(customers, brand) {
    const useSelectedHealthClass = true;
    // We are using a defined health class rather than matching on health questions
    // Remove quotes that are unavailable
    function rank(healthClass) {
      return customers[0].customer.healthRank[healthClass];
    }

    //crazy loop vars - eslint hoisting
    let cix = 0;
    let tix = 0;
    let lix = 0;
    let qix = 0;
    let ladder = null;

    for (cix = 0; cix < customers.length; ++cix) {
      const carrier = {};

      for (tix = 0; tix < customers[cix].quotes.length; ++tix) {
        const healthClass = customers[cix].customer.healthClass;

        for (lix = 0; lix < customers[cix].quotes[tix].length; ++lix) {
          ladder = customers[cix].quotes[tix][lix];

          for (qix = 0; qix < ladder.quote.length; ++qix) {
            const quote = ladder.quote[qix];

            const key = quote.logo + ':' + ladder.face + ':' + ladder.term;

            // Remove other brands if exclusive
            if (brand && quote.logo && !brand.match(quote.logo.replace(/\..*/, ''))) {
              ladder.quote.splice(qix, 1);
              qix--;
            } else if (
              quote.available === 'No' ||
              ((useSelectedHealthClass || quote.available === 'Maybe') && rank(quote.healthClass) > rank(healthClass))
            ) {
              ladder.quote.splice(qix, 1);
              --qix;
            } else if (carrier[key] && rank(quote.healthClass) <= rank(carrier[key])) {
              ladder.quote.splice(qix, 1);
              --qix;
            } else {
              carrier[key] = quote.healthClass;
            }
          }
        }
      }
    }

    // Generate all permutations
    const permutations: any = [];

    for (cix = 0; cix < customers.length; ++cix) {
      // Sort out monthly prices when monthly not quoted
      for (tix = 0; tix < customers[cix].quotes.length; ++tix) {
        for (lix = 0; lix < customers[cix].quotes[tix].length; ++lix) {
          ladder = customers[cix].quotes[tix][lix];

          for (qix = 0; qix < ladder.quote.length; ++qix) {
            const monthly = ladder.quote[qix].monthly.replace(/,/, '');
            const annual = ladder.quote[qix].annual.replace(/,/, '');

            if (isNaN(monthly) || monthly === 0) {
              if (isNaN(annual)) {
                ladder.quote[qix].monthly = 0;
              } else {
                ladder.quote[qix].monthly = annual / 12;
              }
            } else {
              ladder.quote[qix].monthly = Number(monthly);
            }

            ladder.quote[qix].annual = Number(annual);
          }
        }
      }

      permutations.push({
        customer: customers[cix].customer,
        quotes: [],
      });

      //terms
      // Go through long end of ladder and add in other quotes
      if (customers[cix].quotes.length) {
        for (qix = 0; qix < customers[cix].quotes[0][0].quote.length; ++qix) {
          permutations[cix].quotes.push({
            policies: [
              {
                term: customers[cix].quotes[0][0].term,
                face: customers[cix].quotes[0][0].face,
                quote: customers[cix].quotes[0][0].quote[qix],
              },
            ],
          });
        }
      }

      permutations.nonLaddered = permutations.length;
      // Look at each term combination
      for (tix = 1; tix < customers[cix].quotes.length; ++tix) {
        // Extrapolate each combination of long and short end of the ladder
        for (let qix1 = 0; qix1 < customers[cix].quotes[tix][0].quote.length; ++qix1) {
          for (let qix2 = 0; qix2 < customers[cix].quotes[tix][1].quote.length; ++qix2) {
            permutations[cix].quotes.push({
              policies: [
                {
                  term: customers[cix].quotes[tix][0].term,
                  face: customers[cix].quotes[tix][0].face,
                  quote: customers[cix].quotes[tix][0].quote[qix1],
                },
                {
                  term: customers[cix].quotes[tix][1].term,
                  face: customers[cix].quotes[tix][1].face,
                  quote: customers[cix].quotes[tix][1].quote[qix2],
                },
              ],
            });
          }
        }
      }
    }

    return {
      customers: permutations,
    };
  }

  freshenQuotes(quote) {
    if (quote.options !== '') {
      this.findLowestQuote(quote);
    }

    return quote;
  }

  findLowestQuote(quote) {
    quote.options = '';

    const customers = quote.customers;
    let cix = 0;
    let lix = 0;
    let pix = 0;
    let qix = 0;
    let policies = null;
    let ladder = null;
    let quotes = null;
    let customer;

    function isLowest(point, amount) {
      if (customer.lowest[point] > amount) {
        customer.lowest[point] = amount;
      }
    }

    for (; cix < customers.length; ++cix) {
      customer = customers[cix];

      // Look at each term combination and compute total cost
      for (qix = 0; qix < customer.quotes.length; ++qix) {
        // Look at the two legs of the ladder for the term, enrich and total
        let total = 0;

        quotes = customer.quotes[qix];
        policies = quotes.policies;

        for (pix = 0; pix < policies.length; ++pix) {
          const policy = policies[pix];

          policy.monthly = policy.quote.monthly;
          policy.annual = policy.quote.annual;
          policy.logo = policy.quote.logo;
          policy.fee = Number(policy.quote.fee);
          policy.carrierCode = policy.quote.logo.replace(/\..*/, '');
          policy.optionsCost = 0;
          policy.totalMonthly = policy.monthly + policy.optionsCost;
          policy.lifeCost =
            policy.term * policy.monthly * 12 + //(quote.term * quote.fee) +
            policy.term * policy.optionsCost * 12;
          total += policy.lifeCost;
        }

        quotes.lifeCost = total;
      }

      customer.lowest = {
        singleMonthly: 99999999999,
        singleTotal: 99999999999,
        ladderMonthly: 99999999999,
        ladderTotal: 99999999999,
      };

      for (lix = 0; lix < customer.quotes.length; ++lix) {
        ladder = customer.quotes[lix];
        policies = ladder.policies;

        if (policies.length === 1) {
          ladder.costs = [
            {
              start: 1,
              end: policies[0].term,
              cost: policies[0].totalMonthly,
            },
          ];
        } else {
          ladder.costs = [
            {
              start: 1,
              end: policies[1].term,
              cost: policies[0].totalMonthly + policies[1].totalMonthly,
            },
            {
              start: policies[1].term + 1,
              end: policies[0].term,
              cost: policies[0].totalMonthly,
            },
          ];
        }

        if (policies.length === 1) {
          isLowest('singleMonthly', ladder.policies[0].monthly);
          isLowest('singleTotal', ladder.lifeCost);
        } else {
          isLowest('ladderMonthly', ladder.policies[0].monthly + ladder.policies[1].monthly);
          isLowest('ladderTotal', ladder.lifeCost);
        }
      }

      // Kill ladders that are more expensive than lowest single in terms of lifetime or monthly costs
      for (lix = 0; lix < customer.quotes.length; ++lix) {
        ladder = customer.quotes[lix];
        policies = ladder.policies;

        if (
          policies.length === 2 &&
          (ladder.policies[0].monthly + ladder.policies[1].monthly > customer.lowest.singleMonthly ||
            ladder.lifeCost > customer.lowest.singleTotal)
        ) {
          customer.quotes.splice(lix, 1);
          lix--;
        }
      }

      // Peel off non-laddered
      const singlePolicies = [];

      while (customer.quotes[0] && customer.quotes[0].policies.length === 1) {
        singlePolicies.push(customer.quotes[0]);
        customer.quotes.splice(0, 1);
      }

      singlePolicies.sort(function (a, b) {
        return b.policies[0].monthly - a.policies[0].monthly;
      });

      customer.quotes.sort(function (a, b) {
        return a.policies[0].monthly + a.policies[1].monthly - (b.policies[0].monthly + b.policies[1].monthly);
      });

      for (pix = 0; pix < singlePolicies.length; ++pix) {
        customer.quotes.splice(0, 0, singlePolicies[pix]);
      }

      let lowestCost = 99999999999;
      let lowestSingle = 9999999999;

      // Compute the cost of each quote over time
      for (lix = 0; lix < customer.quotes.length; ++lix) {
        ladder = customer.quotes[lix];
        policies = ladder.policies;

        // Determine lowest total cost term combination
        //@@@ Added && policies.length === 1 to filter out ladders for now
        if (ladder.lifeCost < lowestCost && policies.length === 1) {
          customer.selectedPolicy = lix;
          lowestCost = ladder.lifeCost;
        }

        if (policies.length === 1 && ladder.lifeCost < lowestSingle) {
          lowestSingle = ladder.lifeCost;
        }
      }

      // Look at each term combination
      for (qix = 0; qix < customer.quotes.length; ++qix) {
        quotes = customer.quotes[qix];

        if (quotes.policies.length > 1) {
          quotes.savings = Math.max(0, (lowestSingle - quotes.lifeCost) / lowestSingle);
        }
      }
    }

    quote.term = 0;

    for (cix = 0; cix < customers.length; ++cix) {
      quote.term =
        customers[cix].quotes.length > 0 ? Math.max(customers[cix].quotes[0].policies[0].term, quote.term) : 0;
    }

    return quote;
  }

  filterQuotes(incomingQuotes, max, index, type, complaintLevel, paymentPerformanceLevel, ratingLevel, brandSelection) {
    const filterKey =
      this.currentQuoteKey +
      max.toString() +
      index.toString() +
      type +
      complaintLevel.toString() +
      paymentPerformanceLevel.toString() +
      ratingLevel.toString() +
      JSON.stringify(brandSelection);

    if (this.filteredQuotes[filterKey]) {
      return this.filteredQuotes[filterKey];
    }

    const returnQuotes = [];

    if (!incomingQuotes || !incomingQuotes.customers || incomingQuotes.customers.length === 0) {
      return returnQuotes;
    }

    const customer = incomingQuotes.customers[index];

    // Find lowest cost policy in filter
    customer.lowest = {
      singleMonthly: 99999999999,
      singleTotal: 99999999999,
      ladderMonthly: 99999999999,
      ladderTotal: 99999999999,
    };

    function isLowest(point, amount) {
      if (customer.lowest[point] > amount) {
        customer.lowest[point] = amount;
      }
    }

    // Look at each term combination and compute total cost
    for (let qix = 0; qix < customer.quotes.length; ++qix) {
      let include = true;
      const ladder = customer.quotes[qix];
      const policies = ladder.policies;
      let brandMatch = false;

      for (let pix = 0; pix < policies.length && include; ++pix) {
        const policy = policies[pix];
        const company = policy.logo.replace(/\..*/, '');
        const insurer = Insurers.companies[company];

        if (insurer && (brandSelection.All.value || brandSelection[company].value)) {
          brandMatch = true;
        }

        if (insurer) {
          const rating = Insurers.ratingsLevel[insurer.rating];

          policy.insurer = insurer;
          policy.carrier = insurer.fullName;
          policy.shortName = insurer.shortName;
          policy.rating = insurer.rating;
          policy.unsettled = insurer.unsettled;
          policy.complaints = insurer.complaintIndex;

          if (
            rating < ratingLevel ||
            (insurer.complaintIndex >= 0 && insurer.complaintIndex >= complaintLevel) ||
            (insurer.unsettled >= 0 && insurer.unsettled >= paymentPerformanceLevel) ||
            (type === 'nonconvertible' && insurer.convertible) ||
            (type === 'convertible' && !insurer.convertible)
          ) {
            include = false;
          }
        } else {
          include = false;
        }
      }

      if (!brandMatch) {
        include = false;
      }

      if (policies.length === 1) {
        isLowest('singleMonthly', ladder.costs[0].cost);
        isLowest('singleTotal', ladder.lifeCost);
      } else {
        isLowest('ladderMonthly', ladder.costs[0].cost);
        isLowest('ladderTotal', ladder.lifeCost);
      }

      if (include && !((type !== 'ladder' && policies.length > 1) || (type === 'ladder' && policies.length === 1))) {
        returnQuotes.push(ladder);
      }
    }

    // Look at each term combination
    for (let qix2 = 0; qix2 < customer.quotes.length; ++qix2) {
      const quotes = customer.quotes[qix2];

      if (quotes.policies.length > 1) {
        quotes.savings = Math.max(0, (customer.lowest.singleTotal - quotes.lifeCost) / customer.lowest.singleTotal);
      }
    }

    returnQuotes.splice(max);

    this.filteredQuotes[filterKey] = returnQuotes;

    return returnQuotes;
  }

  getSelectedQuote(customers) {
    const quote: any = {
      customers: customers,
    };

    // Calculate the term segments to get price map over time
    const costs = [];
    let yix = 0;

    for (yix = 0; yix < quote.term / 5; ++yix) {
      costs[yix] = {
        cost: 0,
        term: 5,
      };
    }

    // Walk through each 5 year term segment
    for (yix = 0; yix < quote.term / 5; ++yix) {
      for (let pix = 0; pix < customers.length; ++pix) {
        const policies = customers[pix].policies;

        for (let lix = 0; lix < policies.length; ++lix) {
          const step = policies[lix];

          if (yix * 5 < step.term) {
            costs[yix].cost += step.totalMonthly;
          }
        }
      }
    }

    // Squash redundant terms
    for (yix = 0; yix < costs.length; ) {
      if (yix > 0 && costs[yix - 1].cost === costs[yix].cost) {
        costs[yix - 1].term += 5;
        costs.splice(yix, 1);
      } else {
        ++yix;
      }
    }

    let start = 1;

    for (yix = 0; yix < costs.length; ++yix) {
      costs[yix].start = start;
      costs[yix].end = start + costs[yix].term - 1;
      start = costs[yix].end + 1;
    }

    quote.costs = costs;

    return quote;
  }

  areQuotesInProgress() {
    if (!this.quoteCache['inprogress']) {
      return true;
    }

    for (const quote in this.quoteCache['inprogress']) {
      return true;
    }

    return false;
  }

  addHavenQuote(havenQuote, filteredQuotes) {
    const quoteDataStructure = {};

    quoteDataStructure['costs'] = [
      {
        cost: havenQuote.policies[0].monthly,
      },
    ];

    quoteDataStructure['policies'] = havenQuote.policies;

    const allQuotes = filteredQuotes.slice(0, filteredQuotes.length);

    allQuotes.splice(0, 0, quoteDataStructure);

    return allQuotes;
  }

  @remote({ on: 'server' })
  fetchProductQuoteOnServer(
    product: string,
    gender: string,
    rateQCode: string,
    smoker: boolean,
    term: number,
    face: number,
    state: string,
    dob: Date,
    includeWaiver: boolean = true,
    policyNumber?: string,
    rateClass?: string,
    rateClassCodeParam?: string
  ): Promise<PremiumServiceResponse | PremiumResponse> {
    if (amorphicStatic.config.nconf.get('isPremiumServiceEnabled')) {
      let rateClassCode = '';
      if (rateClassCodeParam) {
        rateClassCode = rateClassCodeParam;
      } else {
        const rate = RateClasses.findByQCode(rateQCode);
        rateClassCode = smoker ? rate.smokerCode : rate.code;
      }

      const premiumRequest = {
        inquiries: [
          {
            product,
            gender: gender === 'male' ? ProductApiTypes.Gender.M : ProductApiTypes.Gender.F,
            paymentFrequency: ProductApiTypes.PaymentFrequency.Monthly,
            includeWaiver,
            rateClassCode,
            term,
            face,
            state,
            dob,
          },
        ],
      };

      return PremiumService.getPremiums(premiumRequest);
    } else {
      const requestOptions: PremiumRequest = {
        product,
        productType: Product.products[product].type.code,
        state,
        gender: gender === 'male' ? '1' : '2',
        ratingCode: rateClass,
        dob,
        effectiveDate: Utils.now(),
        term,
        face,
        waiver: includeWaiver,
        flatExtras: [],
        paymentFrequency: 'monthly',
      };
      return ProductServiceClient.getPremium(policyNumber, null, requestOptions);
    }
  }

  fetchQuoteSync(
    product: string,
    gender: string,
    healthClass: string,
    smoker: boolean,
    age: number,
    term: number,
    face: number,
    state: string
  ): QuoteEngineResponse | null {
    // SecReviewed
    const options = {
      product,
      gender,
      healthClass,
      smoker,
      age,
      term,
      face,
      state,
    };

    const quotesEngine = QuoteEngine.new();

    return quotesEngine.getQuoteSync(options);
  }
}
